"use client";

import { Label } from "@/components/ui/label";
import React, { FC, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { formatAddress } from "@/utils/functions";

const CustomInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => {
  return (
    <input
        autoComplete="off"
        className={cn(
          "flex w-full rounded-md border border-input bg-background px-3 py-1 h-9 text-sm",
          className,
        )}
        ref={ref}
        {...props}
      />
  );
});
CustomInput.displayName = "CustomInput";

interface Props {
  placeholder: string;
  location: any;
  inputRef?: any;
  required?: boolean;
  disabled?: boolean;
  onChange?: (e: React.FormEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  selectedLocation?: { id?: number; text: string; lat: number; lng: number };
  optionalLocations?: { id?: number; text: string; lat: number; lng: number }[];
}

const AutocompleteLocation: FC<Props> = ({
  placeholder,
  location,
  inputRef,
  required = true,
  disabled = false,
  onChange = () => {},
  onFocus = () => {},
  selectedLocation,
  optionalLocations = [],
}) => {
  const [pickupSuggestions, setPickupSuggestions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [optionalSuggestions, setOptionalSuggestions] = useState<
    { id?: number; text: string; lat: number; lng: number }[]
  >([]);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const pickupSelectRef = useRef<any>(null);
  const suggestionsRef = useRef<HTMLUListElement>(null);

  // Update input value when selectedLocation prop changes
  useEffect(() => {
    if (selectedLocation && inputRef?.current) {
      inputRef.current.value = selectedLocation.text;
      setPickupSuggestions([]);
      setHighlightedIndex(-1);
    }
  }, [selectedLocation, inputRef]);

  useEffect(() => {
    if (!window.google?.maps?.places) return;

    const autocompleteService =
      new window.google.maps.places.AutocompleteService();
    const placesService = new window.google.maps.places.PlacesService(
      document.createElement("div"),
    );
    const geocoder = new window.google.maps.Geocoder();

    const inputEl = inputRef?.current;
    let debounceTimer: NodeJS.Timeout;

    const extractLatLngFromText = (text: string) => {
      const regex = /(-?\d+\.\d+),\s*(-?\d+\.\d+)/;
      const match = text.match(regex);
      if (match) {
        return {
          lat: parseFloat(match[1]),
          lng: parseFloat(match[2]),
        };
      }
      return null;
    };

    const handleInput = () => {
      const value = inputEl?.value || "";
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const coords = extractLatLngFromText(value);
        if (coords) {
          geocoder.geocode({ location: coords }, (results, status) => {
            if (status === "OK" && results && results.length > 0) {
              const formattedText = results[0].address_components
                ? formatAddress(results[0].address_components)
                : results[0].formatted_address;
              const result = {
                text: formattedText,
                lat: coords.lat,
                lng: coords.lng,
              };
              location(result);
              if (inputEl) {
                inputEl.value = result.text;
                setPickupSuggestions([]);
                setOptionalSuggestions([]);
              }
            }
          });
          return;
        }

        if (!value) {
          // Show all optional locations when input is empty (recent/saved addresses)
          setOptionalSuggestions(optionalLocations);
          setPickupSuggestions([]);
          return;
        }

        // Filter optional locations based on input (only when user is typing)
        const filteredOptional = optionalLocations.filter((loc) =>
          loc.text.toLowerCase().includes(value.toLowerCase()),
        );
        setOptionalSuggestions(filteredOptional);

        const sessionToken = new google.maps.places.AutocompleteSessionToken();
        autocompleteService.getPlacePredictions(
          {
            input: value,
            sessionToken,
            componentRestrictions: {
              country: ["al", "me", "xk", "mk", "gr"], // Albania and its 4 neighboring countries
              // al: Albania, me: Montenegro, xk: Kosovo, mk: North Macedonia, gr: Greece
            },
          },
          (predictions, status) => {
            if (
              status === google.maps.places.PlacesServiceStatus.OK &&
              predictions
            ) {              
              setPickupSuggestions(predictions);
            } else {
              setPickupSuggestions([]);
            }
          },
        );
      }, 300);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const totalSuggestions =
        optionalSuggestions.length + pickupSuggestions.length;
      if (totalSuggestions === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < totalSuggestions - 1 ? prev + 1 : 0,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : totalSuggestions - 1,
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (highlightedIndex >= 0) {
          if (highlightedIndex < optionalSuggestions.length) {
            // Selected from optional locations
            const selected = optionalSuggestions[highlightedIndex];
            location(selected);
            if (inputEl) {
              inputEl.value = selected.text;
              setPickupSuggestions([]);
              setOptionalSuggestions([]);
              setHighlightedIndex(-1);
            }
          } else {
            // Selected from Google Places
            const googleIndex = highlightedIndex - optionalSuggestions.length;
            const selected = pickupSuggestions[googleIndex];
            pickupSelectRef.current?.(selected.place_id);
          }
        }
      }
    };

    inputEl?.addEventListener("input", handleInput);
    inputEl?.addEventListener("keydown", handleKeyDown);

    pickupSelectRef.current = (placeId: string) => {
      placesService.getDetails(
        { placeId, fields: ["geometry", "formatted_address", "address_components", "name"] },
        (place, status) => {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            place?.geometry?.location &&
            place.formatted_address
          ) {
            let formattedText = "";
            
            // Try to format using address components
            if (place.address_components) {
              formattedText = formatAddress(place.address_components);
            }

            // If formatting resulted in empty string, use the place name or formatted_address
            if (!formattedText) {
              // Use place name if available (for establishments like airports)
              formattedText = place.name || place.formatted_address;
            }

            const result = {
              text: formattedText,
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            };
            location(result);
            if (inputEl) {
              inputEl.value = result.text;
              setPickupSuggestions([]);
              setOptionalSuggestions([]);
              setHighlightedIndex(-1);
            }
          }
        },
      );
    };

    return () => {
      inputEl?.removeEventListener("input", handleInput);
      inputEl?.removeEventListener("keydown", handleKeyDown);
      clearTimeout(debounceTimer);
    };
  }, [
    pickupSuggestions,
    optionalSuggestions,
    highlightedIndex,
    optionalLocations,
  ]);

  useEffect(() => {
    // Scroll highlighted item into view
    if (
      highlightedIndex >= 0 &&
      suggestionsRef.current &&
      suggestionsRef.current.children[highlightedIndex]
    ) {
      (
        suggestionsRef.current.children[highlightedIndex] as HTMLElement
      ).scrollIntoView({
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  return (
    <div className="relative">
      <div className="w-full">
        <Label htmlFor="location" className="text-right pb-2">
          {placeholder} {required && "*"}
        </Label>
        <CustomInput
          id="location"
          ref={inputRef ? inputRef : null}
          placeholder={placeholder}
          autoComplete="off"
          required={required}
          disabled={disabled}
          onChange={onChange}
          onFocus={onFocus}
        />
        {(optionalSuggestions.length > 0 || pickupSuggestions.length > 0) && (
          <ul
            ref={suggestionsRef}
            className="max-h-[300px] overflow-auto absolute mt-1 bg-background border rounded shadow z-[9999] w-full"
          >
            {/* Optional locations first */}
            {optionalSuggestions.map((suggestion, index) => (
              <li
                key={`optional-${index}`}
                className={cn(
                  "px-4 py-2 hover:bg-gray-100 cursor-pointer border-b-[1px] border-gray-100",
                  index === highlightedIndex && "bg-gray-200",
                )}
                onClick={() => {
                  location(suggestion);
                  if (inputRef?.current) {
                    inputRef.current.value = suggestion.text;
                    setPickupSuggestions([]);
                    setOptionalSuggestions([]);
                    setHighlightedIndex(-1);
                  }
                }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm text-blue-600">📍</span>
                  <span>{suggestion.text}</span>
                </div>
              </li>
            ))}
            {/* Google Places suggestions */}
            {pickupSuggestions.map((s, index) => {
              const adjustedIndex = index + optionalSuggestions.length;
              return (
                <li
                  key={s.place_id}
                  className={cn(
                    "px-4 py-2 hover:bg-gray-100 cursor-pointer",
                    adjustedIndex === highlightedIndex && "bg-gray-200",
                  )}
                  onClick={() => pickupSelectRef.current?.(s.place_id)}
                >
                  {s.description}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AutocompleteLocation;
